<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import RadioButton from 'primevue/radiobutton';
import ToggleSwitch from 'primevue/toggleswitch';
import Tag from 'primevue/tag';
import InputNumber from 'primevue/inputnumber';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    seller: Object,
    coins: Object,
    token: String,
    exchangeData: Object,
    sellerPrices: Array,
    allSellers: Array,
    allowedSellerIds: Array,
});

// State
const showPassword = ref(false);
const deleteSellerDialog = ref(false);
const tokenCopied = ref(false);
const savingPrices = ref(false);
const globalAdditionValueDisplay = ref(props.seller?.global_addition_value ? props.seller.global_addition_value / 100 : null);
const globalAdditionCoin = ref(props.seller?.global_addition_coin || 'USD');

// API Access Control
const showApiAccessDialog = ref(false);
const savingApiAccess = ref(false);

// Build PickList source/target from allSellers and allowedSellerIds
const allowedSet = new Set(props.allowedSellerIds || []);
const pickListValue = ref([
    (props.allSellers || []).filter(s => !allowedSet.has(s.id)),
    (props.allSellers || []).filter(s => allowedSet.has(s.id)),
]);

function saveApiAccess() {
    savingApiAccess.value = true;
    const allowedIds = pickListValue.value[1].map(s => s.id);
    router.put(route('sellers.updateApiAccess', props.seller.id), {
        is_api_restricted: form.is_api_restricted,
        allowed_seller_ids: allowedIds,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showApiAccessDialog.value = false;
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('API access updated'), life: 3000 });
        },
        onError: () => {
            toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to update API access'), life: 3000 });
        },
        onFinish: () => { savingApiAccess.value = false; },
    });
}

const sameFormulaForAll = ref(false);
const unifiedFormula = ref({
    formula_type: 'A',
    percentage_display: 0,
    fixed_addition_display: null,
    fixed_addition_mode: 'global',
    fixed_addition_currency: 'USD',
});

function getFormulaTypeFromPrice(price) {
    if (price.formula_type) return price.formula_type;
    if (price.percentage_addition === 0) return 'A';
    return price.apply_percentage_to_total ? 'C' : 'B';
}

function getInitialFixedAdditionMode(price) {
    if (price.fixed_addition !== null) return 'custom';
    return 'global';
}

// Initialize seller prices from props
const sellerPricesLocal = ref(
    (props.sellerPrices || []).map(price => ({
        ...price,
        percentage_display: price.percentage_addition / 100, // Convert to display format (e.g., 3000 -> 30%)
        fixed_addition_display: price.fixed_addition !== null ? price.fixed_addition / 100 : null, // null = use default
        fixed_addition_mode: getInitialFixedAdditionMode(price),
        previous_fixed_addition_mode: getInitialFixedAdditionMode(price),
        previous_fixed_addition_display: price.fixed_addition !== null ? price.fixed_addition / 100 : null,
        formula_type: getFormulaTypeFromPrice(price),
    }))
);

// Initialize form
const form = useForm({
    name: props.seller?.name || '',
    email: props.seller?.email || '',
    password: '',
    phone: props.seller?.phone || '',
    linkarme_seller: Boolean(props.seller?.linkarme_seller),
    comission: props.seller?.comission ? props.seller.comission / 100 : '',
    comission_coin: props.seller?.comission_coin || 'BRL',
    is_api_restricted: props.seller?.is_api_restricted ?? false,
    api_discount_percent: props.seller?.api_discount_percent ?? 0,
    requires_payment_before_publishing: props.seller?.requires_payment_before_publishing ?? false,
});

// Submit handler
function submit() {
    // Convert commission and global_addition_value back to cents
    const dataToSubmit = {
        ...form.data(),
        comission: form.comission ? Math.round(form.comission * 100) : null,
        global_addition_value: globalAdditionValueDisplay.value !== null ? Math.round(globalAdditionValueDisplay.value * 100) : null,
    };

    form.transform(() => dataToSubmit).put(route('sellers.update', props.seller.id), {
        preserveScroll: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Seller updated successfully'),
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update seller'),
                life: 3000
            });
        }
    });
}

// Generate random password
function generatePassword() {
    form.password = Math.random().toString(36).slice(-8);
}

// Generate API token
function generateApiToken() {
    router.post(route('sellers.generateApiToken', props.seller.id), {}, {
        preserveState: true,
        onStart: () => showLoading('Generating API token...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('API token generated successfully'),
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to generate API token'),
                life: 3000
            });
        }
    });
}

// Copy API token to clipboard
function copyApiToken() {
    if (props.token) {
        navigator.clipboard.writeText(props.token).then(() => {
            tokenCopied.value = true;
            toast.add({
                severity: 'success',
                summary: $t('Copied'),
                detail: $t('API token copied to clipboard'),
                life: 2000
            });
            setTimeout(() => {
                tokenCopied.value = false;
            }, 2000);
        });
    }
}

// Delete seller
function confirmDeleteSeller() {
    deleteSellerDialog.value = true;
}

function deleteSeller() {
    router.delete(route('sellers.destroy', props.seller.id), {
        onStart: () => showLoading('Deleting seller...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Seller deleted successfully'),
                life: 3000
            });
            showLoading('Loading sellers...');
            router.visit(route('sellers.index'), { onFinish: () => hideLoading() });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to delete seller'),
                life: 3000
            });
        }
    });
}

// Cancel handler
function cancel() {
    showLoading('Loading sellers...');
    router.visit(route('sellers.index'), { onFinish: () => hideLoading() });
}

function validateSellerFixedAddition() {
    const unifiedUsesSellerAddition = sameFormulaForAll.value && unifiedFormula.value.fixed_addition_mode === 'seller';
    const anyPriceUsesSellerAddition = sellerPricesLocal.value.some(price => price.fixed_addition_mode === 'seller');

    if (unifiedUsesSellerAddition || anyPriceUsesSellerAddition) {
        const value = globalAdditionValueDisplay.value;
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            toast.add({
                severity: 'error',
                summary: $t('Validation Error'),
                detail: $t('The "Use seller fixed addition" option is selected, but the Seller Fixed Addition value is empty. Please enter a value or choose a different option.'),
                life: 5000
            });
            return false;
        }
    }

    return true;
}

// Save seller prices
function saveSellerPrices() {
    if (!validateSellerFixedAddition()) {
        return;
    }

    savingPrices.value = true;

    // Convert display values back to storage format
    const pricesToSave = sellerPricesLocal.value.map(price => ({
        exchange_id: price.exchange_id,
        fixed_addition: (price.fixed_addition_mode === 'custom' || price.fixed_addition_mode === 'seller') && price.fixed_addition_display !== null
            ? Math.round(price.fixed_addition_display * 100)
            : null, // null = use global default
        percentage_addition: price.formula_type === 'A' ? 0 : Math.round(price.percentage_display * 100), // Type A has no percentage
        apply_percentage_to_total: price.formula_type === 'C',
        formula_type: price.formula_type,
        is_custom: price.is_custom,
    }));

    router.put(route('sellers.prices.updateAll', props.seller.id), {
        prices: pricesToSave,
        global_addition_value: globalAdditionValueDisplay.value !== null ? Math.round(globalAdditionValueDisplay.value * 100) : null,
        global_addition_coin: globalAdditionCoin.value,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Pricing formulas updated successfully'),
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update pricing formulas'),
                life: 3000
            });
        },
        onFinish: () => {
            savingPrices.value = false;
        }
    });
}

// Reset a price to default
function resetPriceToDefault(index) {
    const price = sellerPricesLocal.value[index];
    price.fixed_addition_display = null;
    price.fixed_addition_mode = 'global';
    price.previous_fixed_addition_mode = 'global';
    price.previous_fixed_addition_display = null;
    price.percentage_display = price.default_percentage_addition / 100;
    price.apply_percentage_to_total = price.default_apply_percentage_to_total;
    price.formula_type = 'A';
    price.is_custom = false;
}

// Mark price as custom when changed
function markAsCustom(index) {
    sellerPricesLocal.value[index].is_custom = true;
}

function applyGlobalAdditionToAll() {
    if (globalAdditionValueDisplay.value === null || globalAdditionValueDisplay.value === undefined) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please enter a value first'),
            life: 3000
        });
        return;
    }

    sellerPricesLocal.value.forEach((price) => {
        const convertedValue = getGlobalAdditionForCurrency(price.currency);
        price.fixed_addition_mode = 'seller';
        price.fixed_addition_display = convertedValue;
        price.is_custom = true;
    });

    toast.add({
        severity: 'success',
        summary: $t('Success'),
        detail: $t('Value applied to all currencies'),
        life: 3000
    });
}

function storePreviousMode(index) {
    const price = sellerPricesLocal.value[index];
    price.previous_fixed_addition_mode = price.fixed_addition_mode;

    if (price.fixed_addition_mode === 'custom' && price.fixed_addition_display !== null) {
        price.previous_fixed_addition_display = price.fixed_addition_display;
    }
}

function onFixedAdditionModeChange(index) {
    const price = sellerPricesLocal.value[index];
    const previousMode = price.previous_fixed_addition_mode;

    if (price.fixed_addition_mode === 'seller') {
        if (globalAdditionValueDisplay.value === null || globalAdditionValueDisplay.value === undefined) {
            toast.add({
                severity: 'warn',
                summary: $t('Warning'),
                detail: $t('Please enter a Seller Fixed Addition Value first'),
                life: 3000
            });
            price.fixed_addition_mode = previousMode || 'global';
            return;
        }

        const convertedValue = getGlobalAdditionForCurrency(price.currency);
        price.fixed_addition_display = convertedValue;
        price.is_custom = true;
    } else if (price.fixed_addition_mode === 'custom') {
        if (price.previous_fixed_addition_display !== null) {
            price.fixed_addition_display = price.previous_fixed_addition_display;
        } else {
            price.fixed_addition_display = price.default_fixed_addition / 100;
        }
        price.is_custom = true;
    } else {
        price.fixed_addition_display = null;
    }

    markAsCustom(index);
}

function onCustomValueChange(index) {
    const price = sellerPricesLocal.value[index];
    price.previous_fixed_addition_display = price.fixed_addition_display;
    markAsCustom(index);
}

// Get formula preview for a price
function getFormulaPreview(price) {
    const percentage = price.percentage_display ? (price.percentage_display / 100) + 1 : 1;
    const percentFormatted = percentage.toFixed(2);

    // Get the fixed value to display
    const fixedValue = (price.fixed_addition_mode === 'custom' || price.fixed_addition_mode === 'seller') && price.fixed_addition_display !== null
        ? price.fixed_addition_display
        : (price.default_fixed_addition / 100);
    const fixedFormatted = fixedValue.toFixed(2);

    if (price.formula_type === 'A') {
        return `= COST + ${fixedFormatted}`;
    } else if (price.formula_type === 'B') {
        return `= (COST × ${percentFormatted}) + ${fixedFormatted}`;
    } else {
        return `= (COST + ${fixedFormatted}) × ${percentFormatted}`;
    }
}

function setFormulaType(index, type) {
    const price = sellerPricesLocal.value[index];
    price.formula_type = type;
    price.apply_percentage_to_total = type === 'C';
    markAsCustom(index);
}

function getExchangeRate(currency) {
    if (!props.exchangeData) return 100;
    const exchange = props.exchangeData.find(e => e.currency === currency);
    return exchange?.value || 100;
}

function convertCurrency(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency || amount === null || amount === undefined) {
        return amount;
    }

    const fromRate = getExchangeRate(fromCurrency);
    const toRate = getExchangeRate(toCurrency);

    return Math.round((amount * toRate / fromRate) * 100) / 100;
}

function setUnifiedFormulaType(type) {
    unifiedFormula.value.formula_type = type;
}

function getUnifiedFixedValueForCurrency(targetCurrency) {
    if (unifiedFormula.value.fixed_addition_mode === 'global') {
        const price = sellerPricesLocal.value.find(p => p.currency === targetCurrency);
        return price ? price.default_fixed_addition / 100 : 0;
    }

    if (unifiedFormula.value.fixed_addition_mode === 'seller') {
        if (globalAdditionValueDisplay.value === null || globalAdditionValueDisplay.value === undefined) {
            return 0;
        }
        return convertCurrency(
            globalAdditionValueDisplay.value,
            globalAdditionCoin.value,
            targetCurrency
        );
    }

    if (unifiedFormula.value.fixed_addition_display === null) {
        return 0;
    }

    return convertCurrency(
        unifiedFormula.value.fixed_addition_display,
        unifiedFormula.value.fixed_addition_currency,
        targetCurrency
    );
}

function getUnifiedFormulaPreviewForCurrency(targetCurrency) {
    const percentage = unifiedFormula.value.percentage_display ? (unifiedFormula.value.percentage_display / 100) + 1 : 1;
    const percentFormatted = percentage.toFixed(2);
    const fixedValue = getUnifiedFixedValueForCurrency(targetCurrency);
    const fixedFormatted = fixedValue.toFixed(2);

    if (unifiedFormula.value.formula_type === 'A') {
        return `= COST + ${fixedFormatted} ${targetCurrency}`;
    } else if (unifiedFormula.value.formula_type === 'B') {
        return `= (COST × ${percentFormatted}) + ${fixedFormatted} ${targetCurrency}`;
    } else {
        return `= (COST + ${fixedFormatted}) × ${percentFormatted} ${targetCurrency}`;
    }
}

function applyUnifiedFormulaToAll() {
    sellerPricesLocal.value.forEach((price) => {
        price.formula_type = unifiedFormula.value.formula_type;
        price.apply_percentage_to_total = unifiedFormula.value.formula_type === 'C';
        price.percentage_display = unifiedFormula.value.percentage_display;

        if (unifiedFormula.value.fixed_addition_mode === 'global') {
            price.fixed_addition_mode = 'global';
            price.fixed_addition_display = null;
        } else if (unifiedFormula.value.fixed_addition_mode === 'seller') {
            price.fixed_addition_mode = 'seller';
            price.fixed_addition_display = getUnifiedFixedValueForCurrency(price.currency);
        } else {
            price.fixed_addition_mode = 'custom';
            price.fixed_addition_display = getUnifiedFixedValueForCurrency(price.currency);
        }

        price.is_custom = true;
    });
}

function resetPricesToInitial() {
    sellerPricesLocal.value = (props.sellerPrices || []).map(price => ({
        ...price,
        percentage_display: price.percentage_addition / 100,
        fixed_addition_display: price.fixed_addition !== null ? price.fixed_addition / 100 : null,
        fixed_addition_mode: getInitialFixedAdditionMode(price),
        previous_fixed_addition_mode: getInitialFixedAdditionMode(price),
        previous_fixed_addition_display: price.fixed_addition !== null ? price.fixed_addition / 100 : null,
        formula_type: getFormulaTypeFromPrice(price),
    }));
}

function resetUnifiedFormula() {
    unifiedFormula.value = {
        formula_type: 'A',
        percentage_display: 0,
        fixed_addition_display: null,
        fixed_addition_mode: 'global',
        fixed_addition_currency: 'USD',
    };
}

watch(sameFormulaForAll, (newValue) => {
    if (newValue) {
        resetUnifiedFormula();
        applyUnifiedFormulaToAll();
    } else {
        resetPricesToInitial();
    }
});

watch(unifiedFormula, () => {
    if (sameFormulaForAll.value) {
        applyUnifiedFormulaToAll();
    }
}, { deep: true });

const dynamicCurrencyOptions = computed(() => {
    if (!props.exchangeData) {
        return [
            { label: 'BRL', value: 'BRL' },
            { label: 'EUR', value: 'EUR' },
            { label: 'USD', value: 'USD' },
            { label: 'GBP', value: 'GBP' },
        ];
    }

    return props.exchangeData.map(exchange => ({
        label: exchange.currency,
        value: exchange.currency
    }));
});

function getGlobalAdditionForCurrency(targetCurrency) {
    if (globalAdditionValueDisplay.value === null || globalAdditionValueDisplay.value === undefined) {
        return null;
    }

    return convertCurrency(
        globalAdditionValueDisplay.value,
        globalAdditionCoin.value,
        targetCurrency
    );
}

watch(globalAdditionValueDisplay, (newValue) => {
    if (newValue !== null && newValue !== undefined) {
        if (sameFormulaForAll.value && unifiedFormula.value.fixed_addition_mode === 'seller') {
            applyUnifiedFormulaToAll();
        } else {
            sellerPricesLocal.value.forEach((price) => {
                if (price.fixed_addition_mode === 'seller') {
                    price.fixed_addition_display = getGlobalAdditionForCurrency(price.currency);
                }
            });
        }
    }
});

watch(globalAdditionCoin, () => {
    if (globalAdditionValueDisplay.value !== null && globalAdditionValueDisplay.value !== undefined) {
        if (sameFormulaForAll.value && unifiedFormula.value.fixed_addition_mode === 'seller') {
            applyUnifiedFormulaToAll();
        } else {
            sellerPricesLocal.value.forEach((price) => {
                if (price.fixed_addition_mode === 'seller') {
                    price.fixed_addition_display = getGlobalAdditionForCurrency(price.currency);
                }
            });
        }
    }
});

defineOptions({
    layout: AdminMainLayout,
});
</script>

<template>
    <div>
        <!-- Header Card -->
        <Card class="mb-6">
            <template #content>
                <div class="flex justify-between items-center">
                    <div class="flex items-center gap-3">
                        <Button
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            text
                            rounded
                            @click="cancel"
                            v-tooltip.top="$t('Back to list')"
                        />
                        <div class="flex items-center gap-3">
                            <i class="pi pi-user-edit text-2xl text-primary-500"></i>
                            <div>
                                <h2 class="text-xl font-bold">{{ $t('Edit seller') }}</h2>
                                <span class="text-sm text-surface-500">{{ $t('Update seller information') }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="flex gap-2">
                        <Button
                            icon="pi pi-eye"
                            severity="info"
                            text
                            rounded
                            @click="() => { showLoading('Loading seller details...'); router.visit(route('sellers.show', seller.id), { onFinish: () => hideLoading() }); }"
                            v-tooltip.top="$t('View seller')"
                        />
                        <Button
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            rounded
                            @click="confirmDeleteSeller"
                            v-tooltip.top="$t('Delete seller')"
                        />
                    </div>
                </div>
            </template>
        </Card>

        <!-- Main Form Card -->
        <Card>
            <template #content>
                <form @submit.prevent="submit">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <!-- Name -->
                        <div class="field">
                            <label for="name" class="font-medium mb-2 block">
                                {{ $t('Username') }}
                                <span class="text-red-500">*</span>
                            </label>
                            <InputText
                                id="name"
                                v-model="form.name"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.name }"
                                autocomplete="name"
                            />
                            <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>
                        </div>

                        <!-- Email -->
                        <div class="field">
                            <label for="email" class="font-medium mb-2 block">
                                {{ $t('Email') }}
                                <span class="text-red-500">*</span>
                            </label>
                            <InputText
                                id="email"
                                v-model="form.email"
                                type="email"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.email }"
                                autocomplete="email"
                            />
                            <small v-if="form.errors.email" class="p-error">{{ form.errors.email }}</small>
                        </div>

                        <!-- Password -->
                        <div class="field">
                            <label for="password" class="font-medium mb-2 block">
                                {{ $t('Password') }}
                            </label>
                            <div class="flex w-full gap-2">
                                <InputText
                                    id="password"
                                    v-model="form.password"
                                    :type="showPassword ? 'text' : 'password'"
                                    class="flex-1"
                                    :class="{ 'p-invalid': form.errors.password }"
                                    autocomplete="new-password"
                                    :placeholder="$t('Leave blank to keep current password')"
                                />
                                <Button
                                    type="button"
                                    :icon="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
                                    @click="showPassword = !showPassword"
                                    severity="secondary"
                                    v-tooltip.top="showPassword ? $t('Hide password') : $t('Show password')"
                                />
                                <Button
                                    type="button"
                                    icon="pi pi-refresh"
                                    @click="generatePassword"
                                    severity="info"
                                    v-tooltip.top="$t('Generate password')"
                                />
                            </div>
                            <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
                            <small class="text-surface-500">{{ $t('Leave blank to keep current password') }}</small>
                        </div>

                        <!-- Phone -->
                        <div class="field">
                            <label for="phone" class="font-medium mb-2 block">
                                {{ $t('Phone') }}
                            </label>
                            <InputText
                                id="phone"
                                v-model="form.phone"
                                type="tel"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.phone }"
                                autocomplete="tel"
                            />
                            <small v-if="form.errors.phone" class="p-error">{{ form.errors.phone }}</small>
                        </div>

                        <!-- Commission -->
                        <!-- <div class="field">
                            <label for="comission" class="font-medium mb-2 block">
                                {{ $t('Commission') }}
                            </label>
                            <div class="flex w-full gap-2">
                                <InputNumber
                                    id="comission"
                                    v-model="form.comission"
                                    mode="currency"
                                    :currency="form.comission_coin"
                                    locale="pt-BR"
                                    class="flex-1"
                                    :class="{ 'p-invalid': form.errors.comission }"
                                />
                                <Dropdown
                                    v-model="form.comission_coin"
                                    :options="dynamicCurrencyOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="pt-1 w-32"
                                />
                            </div>
                            <small v-if="form.errors.comission" class="p-error">{{ form.errors.comission }}</small>
                        </div> -->

                        <!-- API Token -->
                        <div class="field">
                            <label for="token" class="font-medium mb-2 block">
                                {{ $t('API Key') }}
                                <span v-if="seller.tokens && seller.tokens.length > 0" class="text-surface-500">
                                    ({{ seller.tokens.length }} {{ $t('active') }})
                                </span>
                            </label>
                            <div class="flex w-full gap-2">
                                <InputText
                                    id="token"
                                    :value="token || ''"
                                    readonly
                                    class="flex-1 font-mono"
                                    :placeholder="$t('No token generated')"
                                />
                                <Button
                                    v-if="token"
                                    type="button"
                                    :icon="tokenCopied ? 'pi pi-check' : 'pi pi-copy'"
                                    @click="copyApiToken"
                                    :severity="tokenCopied ? 'success' : 'secondary'"
                                    v-tooltip.top="$t('Copy token')"
                                />
                                <Button
                                    type="button"
                                    icon="pi pi-refresh"
                                    @click="generateApiToken"
                                    severity="info"
                                    v-tooltip.top="$t('Generate new token')"
                                />
                            </div>
                        </div>

                        <!-- API Discount -->
                        <div class="field mt-4">
                            <label for="api_discount_percent" class="font-medium mb-2 block">
                                {{ $t('API Discount') }} (%)
                            </label>
                            <InputNumber
                                id="api_discount_percent"
                                v-model="form.api_discount_percent"
                                :min="0"
                                :max="100"
                                :minFractionDigits="0"
                                :maxFractionDigits="2"
                                suffix=" %"
                                class="w-full md:w-64"
                                :class="{ 'p-invalid': form.errors.api_discount_bps }"
                            />
                            <small class="text-surface-500 block mt-1">
                                {{ $t('Discount applied to all prices returned by the marketplace API when this seller authenticates with their API key. Final prices are rounded down to the nearest 0.05.') }}
                            </small>
                            <small v-if="form.errors.api_discount_bps" class="p-error block">{{ form.errors.api_discount_bps }}</small>
                        </div>
                    </div>

                    <!-- API Access Control -->
                    <div class="mt-6 pt-6 border-t border-surface-200">
                        <h3 class="text-lg font-semibold mb-4">{{ $t('API Access Control') }}</h3>

                        <div class="flex items-center gap-3 mb-4">
                            <Checkbox
                                id="is_api_restricted"
                                v-model="form.is_api_restricted"
                                :binary="true"
                            />
                            <label for="is_api_restricted" class="font-medium cursor-pointer">
                                {{ $t('Undisclosed seller') }}
                            </label>
                        </div>
                        <small class="text-surface-500 block mb-4">
                            {{ $t('When checked, this seller\'s offers will be hidden from ALL API consumers') }}
                        </small>

                        <div class="flex items-center gap-3">
                            <Button
                                type="button"
                                :label="$t('Configure API Access') + ` (${pickListValue[1].length})`"
                                icon="pi pi-users"
                                severity="secondary"
                                @click="showApiAccessDialog = true"
                            />
                        </div>
                    </div>

                    <!-- Payment Settings -->
                    <div class="mt-6 pt-6 border-t border-surface-200">
                        <h3 class="text-lg font-semibold mb-4">{{ $t('Payment Settings') }}</h3>
                        <div class="flex items-center gap-3 mb-2">
                            <Checkbox
                                id="requires_payment_before_publishing"
                                v-model="form.requires_payment_before_publishing"
                                :binary="true"
                            />
                            <label for="requires_payment_before_publishing" class="font-medium cursor-pointer">
                                {{ $t('Requires payment before publishing') }}
                            </label>
                        </div>
                        <small class="text-surface-500 block">
                            {{ $t('When checked, a warning will appear before sending content to this seller\'s portal if payment has not been confirmed.') }}
                        </small>
                    </div>

                    <!-- Additional Information -->
                    <div class="mt-6 pt-6">
                        <h3 class="text-lg font-semibold mb-4">{{ $t('Account Information') }}</h3>
                        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label class="text-sm text-surface-500">{{ $t('Created at') }}</label>
                                <p class="font-medium">{{ new Date(seller.created_at).toLocaleString() }}</p>
                            </div>
                            <div>
                                <label class="text-sm text-surface-500">{{ $t('Updated at') }}</label>
                                <p class="font-medium">{{ new Date(seller.updated_at).toLocaleString() }}</p>
                            </div>
                            <div>
                                <label class="text-sm text-surface-500">{{ $t('Account ID') }}</label>
                                <p class="font-medium font-mono">{{ seller.id }}</p>
                            </div>
                            <!-- <div>
                                <div class="flex items-center gap-2 mt-1">
                                    <Checkbox
                                        id="linkarme_seller"
                                        v-model="form.linkarme_seller"
                                        :binary="true"
                                    />
                                    <label for="linkarme_seller" class="font-medium">
                                        {{ $t('Linkarme Seller') }}
                                    </label>
                                </div>
                            </div> -->
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex justify-end gap-2 mt-6 pt-6">
                        <Button
                            type="button"
                            :label="$t('Cancel')"
                            severity="secondary"
                            @click="cancel"
                        />
                        <Button
                            type="submit"
                            :label="$t('Save changes')"
                            icon="pi pi-check"
                            :loading="form.processing"
                            :disabled="form.processing"
                        />
                    </div>
                </form>
            </template>
        </Card>

        <!-- Pricing Formulas Card -->
        <Card class="mt-6" v-if="sellerPricesLocal.length > 0">
            <template #content>
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-calculator text-2xl text-primary-500"></i>
                        <div>
                            <h3 class="text-lg font-bold">{{ $t('Pricing Formulas') }}</h3>
                            <span class="text-sm text-surface-500">{{ $t('Custom pricing formulas for this seller per currency') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Global Addition Value -->
                <div class="mb-6 p-4 bg-surface-50 rounded-lg border border-surface-200">
                    <div class="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div class="flex-1">
                            <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Seller fixed addition', { seller: seller.name }) }}</label>
                            <div class="flex flex-col sm:flex-row gap-2">
                                <div class="flex flex-col sm:flex-row gap-2 flex-1">
                                    <InputNumber
                                        v-model="globalAdditionValueDisplay"
                                        mode="decimal"
                                        locale="en-US"
                                        :useGrouping="true"
                                        :minFractionDigits="2"
                                        :maxFractionDigits="2"
                                        class="flex-1"
                                        :placeholder="$t('Enter value')"
                                    />
                                    <Dropdown
                                        v-model="globalAdditionCoin"
                                        :options="dynamicCurrencyOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        class="w-full sm:w-28"
                                    />
                                </div>
                                <Button
                                    :label="$t('Apply to All')"
                                    icon="pi pi-check-circle"
                                    severity="secondary"
                                    @click="applyGlobalAdditionToAll"
                                    class="w-full sm:w-auto"
                                />
                            </div>
                            <small class="text-surface-500 mt-1 block">{{ $t('This value will be applied to the Fixed Addition of all currencies when you click Apply to All') }}</small>
                        </div>
                    </div>
                </div>

                <!-- Toggle: Same formula for every exchange -->
                <div class="mb-6 flex items-center gap-3">
                    <ToggleSwitch v-model="sameFormulaForAll" />
                    <label class="text-sm font-medium text-surface-700 cursor-pointer" @click="sameFormulaForAll = !sameFormulaForAll">
                        {{ $t('Same formula for every exchange') }}
                    </label>
                </div>

                <!-- Unified Formula Card (when toggle is active) -->
                <div v-if="sameFormulaForAll" class="border border-primary-500 bg-primary-50 rounded-lg p-4 mb-6">
                    <div class="flex justify-between items-center mb-4">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-globe text-primary-500"></i>
                            <span class="font-bold text-lg">{{ $t('All Currencies') }}</span>
                            <Tag :value="$t('Unified')" severity="primary" class="text-xs" />
                        </div>
                    </div>

                    <!-- Formula Type Selection -->
                    <div class="mb-4">
                        <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Formula Type') }}</label>
                        <div class="flex gap-2">
                            <div
                                class="flex-1 p-3 border rounded-lg cursor-pointer transition-all text-center"
                                :class="unifiedFormula.formula_type === 'A' ? 'border-primary-500 bg-white' : 'border-surface-200 hover:border-surface-300'"
                                @click="setUnifiedFormulaType('A')"
                            >
                                <div class="text-xs font-semibold mb-1">{{ $t('Type A') }}</div>
                                <code class="text-xs">COST + FIXED</code>
                            </div>

                            <div
                                class="flex-1 p-3 border rounded-lg cursor-pointer transition-all text-center"
                                :class="unifiedFormula.formula_type === 'B' ? 'border-primary-500 bg-white' : 'border-surface-200 hover:border-surface-300'"
                                @click="setUnifiedFormulaType('B')"
                            >
                                <div class="text-xs font-semibold mb-1">{{ $t('Type B') }}</div>
                                <code class="text-xs">(COST × %) + FIXED</code>
                            </div>

                            <div
                                class="flex-1 p-3 border rounded-lg cursor-pointer transition-all text-center"
                                :class="unifiedFormula.formula_type === 'C' ? 'border-primary-500 bg-white' : 'border-surface-200 hover:border-surface-300'"
                                @click="setUnifiedFormulaType('C')"
                            >
                                <div class="text-xs font-semibold mb-1">{{ $t('Type C') }}</div>
                                <code class="text-xs">(COST + FIXED) × %</code>
                            </div>
                        </div>
                    </div>

                    <!-- Percentage Input -->
                    <div class="mb-4" v-if="unifiedFormula.formula_type !== 'A'">
                        <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Percentage Addition') }}</label>
                        <InputNumber
                            v-model="unifiedFormula.percentage_display"
                            mode="decimal"
                            :maxFractionDigits="1"
                            suffix="%"
                            class="w-full"
                        />
                    </div>

                    <!-- Fixed Addition Input -->
                    <div class="mb-4">
                        <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Fixed Addition') }}</label>
                        <div class="flex flex-wrap gap-4 mb-3">
                            <div class="flex items-center gap-2">
                                <RadioButton
                                    v-model="unifiedFormula.fixed_addition_mode"
                                    inputId="unified-mode-global"
                                    value="global"
                                />
                                <label for="unified-mode-global" class="text-xs text-surface-600 cursor-pointer">{{ $t('Use Global') }}</label>
                            </div>
                            <div class="flex items-center gap-2">
                                <RadioButton
                                    v-model="unifiedFormula.fixed_addition_mode"
                                    inputId="unified-mode-seller"
                                    value="seller"
                                    :disabled="!globalAdditionValueDisplay"
                                />
                                <label for="unified-mode-seller" class="text-xs cursor-pointer" :class="globalAdditionValueDisplay ? 'text-surface-600' : 'text-surface-400'">{{ $t('Use seller fixed addition', { seller: seller.name }) }}</label>
                            </div>
                            <div class="flex items-center gap-2">
                                <RadioButton
                                    v-model="unifiedFormula.fixed_addition_mode"
                                    inputId="unified-mode-custom"
                                    value="custom"
                                />
                                <label for="unified-mode-custom" class="text-xs text-surface-600 cursor-pointer">{{ $t('Custom value') }}</label>
                            </div>
                        </div>
                        <div v-if="unifiedFormula.fixed_addition_mode === 'custom'" class="flex flex-col sm:flex-row gap-2">
                            <InputNumber
                                v-model="unifiedFormula.fixed_addition_display"
                                mode="decimal"
                                :minFractionDigits="2"
                                :maxFractionDigits="2"
                                class="flex-1"
                                :placeholder="$t('Enter value')"
                            />
                            <Dropdown
                                v-model="unifiedFormula.fixed_addition_currency"
                                :options="dynamicCurrencyOptions"
                                optionLabel="label"
                                optionValue="value"
                                class="w-full sm:w-28"
                            />
                        </div>
                        <div v-else-if="unifiedFormula.fixed_addition_mode === 'seller'" class="p-2 bg-surface-50 rounded border text-sm text-surface-600">
                            {{ $t('Using seller fixed addition', { seller: seller.name }) }}: {{ globalAdditionValueDisplay?.toFixed(2) }} {{ globalAdditionCoin }}
                        </div>
                        <div v-else class="p-2 bg-surface-50 rounded border text-sm text-surface-600">
                            {{ $t('Using global default for each currency') }}
                        </div>
                    </div>

                    <!-- Formula Preview for all currencies -->
                    <div class="bg-surface-100 p-3 rounded-lg">
                        <label class="text-xs font-medium text-surface-500 block mb-2">{{ $t('Formula Preview') }}</label>
                        <div class="space-y-1">
                            <div v-for="price in sellerPricesLocal" :key="price.exchange_id" class="flex items-center gap-2">
                                <span class="font-semibold text-xs w-10">{{ price.currency }}:</span>
                                <code class="text-xs text-surface-700">{{ getUnifiedFormulaPreviewForCurrency(price.currency) }}</code>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Individual currency cards (when toggle is inactive) -->
                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div
                        v-for="(price, index) in sellerPricesLocal"
                        :key="price.exchange_id"
                        class="border rounded-lg p-4"
                        :class="price.is_custom ? 'border-primary-500 bg-primary-50' : 'border-surface-200'"
                    >
                        <div class="flex justify-between items-center mb-4">
                            <div class="flex items-center gap-2">
                                <span class="font-bold text-lg">{{ price.currency }}</span>
                                <Tag
                                    v-if="price.is_custom"
                                    :value="$t('Custom')"
                                    severity="primary"
                                    class="text-xs"
                                />
                                <Tag
                                    v-else
                                    :value="$t('Default')"
                                    severity="secondary"
                                    class="text-xs"
                                />
                            </div>
                            <Button
                                v-if="price.is_custom"
                                icon="pi pi-refresh"
                                severity="secondary"
                                text
                                rounded
                                size="small"
                                v-tooltip.top="$t('Reset to default')"
                                @click="resetPriceToDefault(index)"
                            />
                        </div>

                        <!-- Formula Type Selection -->
                        <div class="mb-4">
                            <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Formula Type') }}</label>
                            <div class="flex gap-2">
                                <div
                                    class="flex-1 p-3 border rounded-lg cursor-pointer transition-all text-center"
                                    :class="price.formula_type === 'A' ? 'border-primary-500 bg-white' : 'border-surface-200 hover:border-surface-300'"
                                    @click="setFormulaType(index, 'A')"
                                >
                                    <div class="text-xs font-semibold mb-1">{{ $t('Type A') }}</div>
                                    <code class="text-xs">COST + FIXED</code>
                                </div>

                                <div
                                    class="flex-1 p-3 border rounded-lg cursor-pointer transition-all text-center"
                                    :class="price.formula_type === 'B' ? 'border-primary-500 bg-white' : 'border-surface-200 hover:border-surface-300'"
                                    @click="setFormulaType(index, 'B')"
                                >
                                    <div class="text-xs font-semibold mb-1">{{ $t('Type B') }}</div>
                                    <code class="text-xs">(COST × %) + FIXED</code>
                                </div>

                                <div
                                    class="flex-1 p-3 border rounded-lg cursor-pointer transition-all text-center"
                                    :class="price.formula_type === 'C' ? 'border-primary-500 bg-white' : 'border-surface-200 hover:border-surface-300'"
                                    @click="setFormulaType(index, 'C')"
                                >
                                    <div class="text-xs font-semibold mb-1">{{ $t('Type C') }}</div>
                                    <code class="text-xs">(COST + FIXED) × %</code>
                                </div>
                            </div>
                        </div>

                        <!-- Percentage Input -->
                        <div class="mb-4" v-if="price.formula_type !== 'A'">
                            <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Percentage Addition') }}</label>
                            <InputNumber
                                v-model="price.percentage_display"
                                mode="decimal"
                                :maxFractionDigits="1"
                                suffix="%"
                                class="w-full"
                                @input="markAsCustom(index)"
                            />
                        </div>

                        <!-- Fixed Addition Input -->
                        <div class="mb-4">
                            <label class="text-sm font-medium text-surface-600 block mb-2">{{ $t('Fixed Addition') }}</label>
                            <div class="flex flex-wrap gap-4 mb-3">
                                <div class="flex items-center gap-2">
                                    <RadioButton
                                        v-model="price.fixed_addition_mode"
                                        :inputId="`mode-global-${index}`"
                                        value="global"
                                        @click="storePreviousMode(index)"
                                        @change="onFixedAdditionModeChange(index)"
                                    />
                                    <label :for="`mode-global-${index}`" class="text-xs text-surface-600 cursor-pointer">{{ $t('Use Global') }}</label>
                                </div>
                                <div class="flex items-center gap-2">
                                    <RadioButton
                                        v-model="price.fixed_addition_mode"
                                        :inputId="`mode-seller-${index}`"
                                        value="seller"
                                        :disabled="!globalAdditionValueDisplay"
                                        @click="storePreviousMode(index)"
                                        @change="onFixedAdditionModeChange(index)"
                                    />
                                    <label :for="`mode-seller-${index}`" class="text-xs cursor-pointer" :class="globalAdditionValueDisplay ? 'text-surface-600' : 'text-surface-400'">{{ $t('Use seller fixed addition', { seller: seller.name }) }}</label>
                                </div>
                                <div class="flex items-center gap-2">
                                    <RadioButton
                                        v-model="price.fixed_addition_mode"
                                        :inputId="`mode-custom-${index}`"
                                        value="custom"
                                        @click="storePreviousMode(index)"
                                        @change="onFixedAdditionModeChange(index)"
                                    />
                                    <label :for="`mode-custom-${index}`" class="text-xs text-surface-600 cursor-pointer">{{ $t('Custom value') }}</label>
                                </div>
                            </div>
                            <InputNumber
                                v-if="price.fixed_addition_mode === 'custom' || price.fixed_addition_mode === 'seller'"
                                v-model="price.fixed_addition_display"
                                mode="currency"
                                :currency="price.currency"
                                locale="en-US"
                                class="w-full"
                                :disabled="price.fixed_addition_mode === 'seller'"
                                @input="onCustomValueChange(index)"
                            />
                            <div v-else class="p-2 bg-surface-50 rounded border text-sm text-surface-600">
                                {{ $t('Using global default') }}: {{ (price.default_fixed_addition / 100).toFixed(2) }} {{ price.currency }}
                            </div>
                        </div>

                        <!-- Formula Preview -->
                        <div class="bg-surface-100 p-3 rounded-lg">
                            <label class="text-xs font-medium text-surface-500 block mb-1">{{ $t('Formula Preview') }}</label>
                            <code class="text-sm">{{ getFormulaPreview(price) }}</code>
                        </div>
                    </div>
                </div>
                <!-- Form Actions -->
                <div class="flex justify-end gap-2 mt-6 pt-6">
                    <Button
                        type="button"
                        :label="$t('Cancel')"
                        severity="secondary"
                        @click="cancel"
                    />
                    <Button
                        :label="$t('Save Formulas')"
                        icon="pi pi-save"
                        :loading="savingPrices"
                        :disabled="savingPrices"
                        @click="saveSellerPrices"
                    />
                </div>
            </template>
        </Card>

        <!-- API Access Dialog -->
        <Dialog
            v-model:visible="showApiAccessDialog"
            :header="$t('Configure API Access for') + ` ${seller.name}`"
            :modal="true"
            :style="{ width: '750px' }"
        >
            <p class="text-surface-500 text-sm mb-4">
                {{ $t('Select which sellers this seller can see via API. Non-disclosable sellers are excluded automatically.') }}
            </p>
            <PickList v-model="pickListValue" dataKey="id" breakpoint="575px">
                <template #sourceheader>{{ $t('Available Sellers') }}</template>
                <template #targetheader>{{ $t('Allowed Sellers') }}</template>
                <template #item="{ item }">
                    <span>{{ item.name }}</span>
                </template>
            </PickList>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="showApiAccessDialog = false"
                />
                <Button
                    :label="$t('Save API Access')"
                    icon="pi pi-save"
                    @click="saveApiAccess"
                    :loading="savingApiAccess"
                />
            </template>
        </Dialog>

        <!-- Delete Confirmation Dialog -->
        <Dialog
            v-model:visible="deleteSellerDialog"
            :style="{ width: '450px' }"
            :header="$t('Confirm Delete')"
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span>
                    {{ $t('Are you sure you want to delete seller') }} <b>{{ seller.name }}</b>?
                    {{ $t('This action cannot be undone.') }}
                </span>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="deleteSellerDialog = false"
                />
                <Button
                    :label="$t('Delete')"
                    icon="pi pi-trash"
                    severity="danger"
                    @click="deleteSeller"
                />
            </template>
        </Dialog>
    </div>
</template>
